/**
 * Notes React Query Hooks
 *
 * Custom hooks for manager notes and meeting notes.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  notesApi,
  type CreateManagerNoteInput,
  type UpdateManagerNoteInput,
  type CreateMeetingNoteInput,
  type UpdateMeetingNoteInput,
} from '../../lib/notesApi';

// Query keys
export const noteKeys = {
  all: ['notes'] as const,
  manager: () => [...noteKeys.all, 'manager'] as const,
  managerList: (employeeId?: number) => [...noteKeys.manager(), 'list', employeeId] as const,
  managerDetail: (id: number) => [...noteKeys.manager(), 'detail', id] as const,
  meeting: () => [...noteKeys.all, 'meeting'] as const,
  meetingList: (employeeId?: number) => [...noteKeys.meeting(), 'list', employeeId] as const,
  meetingShared: () => [...noteKeys.meeting(), 'shared'] as const,
  meetingDetail: (id: number) => [...noteKeys.meeting(), 'detail', id] as const,
};

// Manager Notes Hooks

/**
 * Hook to fetch manager notes
 */
export function useManagerNotes(employeeId?: number) {
  return useQuery({
    queryKey: noteKeys.managerList(employeeId),
    queryFn: () => notesApi.getManagerNotes(employeeId),
  });
}

/**
 * Hook to fetch single manager note
 */
export function useManagerNote(id: number) {
  return useQuery({
    queryKey: noteKeys.managerDetail(id),
    queryFn: () => notesApi.getManagerNote(id),
    enabled: id > 0,
  });
}

/**
 * Hook to create manager note
 */
export function useCreateManagerNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateManagerNoteInput) => notesApi.createManagerNote(input),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: noteKeys.manager() });
      queryClient.invalidateQueries({ queryKey: noteKeys.managerList(employeeId) });
    },
  });
}

/**
 * Hook to update manager note
 */
export function useUpdateManagerNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateManagerNoteInput }) =>
      notesApi.updateManagerNote(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: noteKeys.manager() });
      queryClient.invalidateQueries({ queryKey: noteKeys.managerDetail(id) });
    },
  });
}

/**
 * Hook to delete manager note
 */
export function useDeleteManagerNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notesApi.deleteManagerNote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.manager() });
    },
  });
}

// Meeting Notes Hooks

/**
 * Hook to fetch meeting notes
 */
export function useMeetingNotes(employeeId?: number) {
  return useQuery({
    queryKey: noteKeys.meetingList(employeeId),
    queryFn: () => notesApi.getMeetingNotes(employeeId),
  });
}

/**
 * Hook to fetch shared meeting notes for employee
 */
export function useSharedMeetingNotes() {
  return useQuery({
    queryKey: noteKeys.meetingShared(),
    queryFn: () => notesApi.getSharedMeetingNotes(),
  });
}

/**
 * Hook to fetch single meeting note
 */
export function useMeetingNote(id: number) {
  return useQuery({
    queryKey: noteKeys.meetingDetail(id),
    queryFn: () => notesApi.getMeetingNote(id),
    enabled: id > 0,
  });
}

/**
 * Hook to create meeting note
 */
export function useCreateMeetingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMeetingNoteInput) => notesApi.createMeetingNote(input),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: noteKeys.meeting() });
      queryClient.invalidateQueries({ queryKey: noteKeys.meetingList(employeeId) });
    },
  });
}

/**
 * Hook to update meeting note
 */
export function useUpdateMeetingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateMeetingNoteInput }) =>
      notesApi.updateMeetingNote(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: noteKeys.meeting() });
      queryClient.invalidateQueries({ queryKey: noteKeys.meetingDetail(id) });
    },
  });
}

/**
 * Hook to delete meeting note
 */
export function useDeleteMeetingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notesApi.deleteMeetingNote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.meeting() });
    },
  });
}
